import { Grid2, Stack, Button, Box, Typography, SxProps } from "@mui/material";
import classNames from "classnames";
import { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import EastIcon from "@mui/icons-material/East";
import "./styles.scss";
import { HashLink } from "react-router-hash-link";
import { sectionPadding } from "../../../utils";
import CheckIcon from "@mui/icons-material/Check";

export const SplitScreenBlock = ({
  title,
  subTitle,
  image,
  video,
  description,
  linkText,
  to,
  onClick,
  className,
  reverse = false,
  component,
  buttonStyle = "outlined",
  titleClassName,
  backgroundImage = false,
  list,
  sx,
  subTitle2,
  linkOnContainer = false,
}: {
  title: string;
  subTitle?: string;
  image?: string;
  video?: string;
  description?: string;
  linkText?: string;
  to?: string;
  onClick?: () => void;
  className?: string;
  reverse?: boolean;
  component?: ReactNode;
  buttonStyle?: "contained" | "outlined";
  titleClassName?: string;
  backgroundImage?: boolean;
  list?: string[];
  sx?: SxProps;
  subTitle2?: string;
  linkOnContainer?: boolean;
}) => {
  const breakpoints = {
    xs: 12,
    sm: 6,
  };
  const navigate = useNavigate();

  const imageBlock = (
    <Grid2
      style={{
        ...(backgroundImage && {
          background: `url(${image}) no-repeat left center / cover`,
        }),
      }}
      className={classNames(
        "image-container",
        backgroundImage && "background-image"
      )}
      size={breakpoints}
    >
      {!backgroundImage && <img src={image} alt={title} />}
    </Grid2>
  );

  const videoBlock = (
    <Grid2
      className="video-container text-center"
      size={breakpoints}
      justifyContent="center"
      alignContent="center"
      alignItems="center"
    >
      <video
        style={{ maxWidth: "100%", width: "600px", margin: "0 auto" }}
        autoPlay
        muted
        loop
        playsInline
      >
        <source src={video} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </Grid2>
  );

  const componentBlock = (
    <Grid2
      className="component-container"
      size={breakpoints}
      justifyContent="space-around"
      alignContent="space-around"
      alignItems="space-around"
    >
      <Stack justifyContent="space-around" alignItems="center">
        {component}
      </Stack>
    </Grid2>
  );

  const contentBlock = (
    <Grid2 className="content-container" size={breakpoints}>
      <Box
        className="inner-content"
        sx={{
          ...sectionPadding,
        }}
      >
        {subTitle2 && (
          <Typography
            variant="h6"
            component="h3"
            className={classNames("title", titleClassName)}
            sx={{
              mb: "16px",
            }}
          >
            {subTitle2}
          </Typography>
        )}
        <Typography
          variant="h5"
          component="h3"
          className={classNames("title text-pink", titleClassName)}
          sx={{
            mb: "16px",
          }}
        >
          {title}
        </Typography>
        {subTitle && (
          <Typography
            variant="h6"
            component="h3"
            className={classNames("title", titleClassName)}
            sx={{
              mb: "16px",
            }}
          >
            {subTitle}
          </Typography>
        )}
        {description && (
          <Typography
            variant="body1"
            className="description"
            sx={{
              mb: "28px",
            }}
          >
            {description}
          </Typography>
        )}
        {list && (
          <Box>
            {list.map((item) => {
              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ mb: "8px" }}
                  spacing={2}
                >
                  <CheckIcon className="text-pink" />
                  <Typography variant="body1">{item}</Typography>
                </Stack>
              );
            })}
          </Box>
        )}
        {to && linkText && (
          <Button
            variant={buttonStyle}
            endIcon={<EastIcon />}
            component={to.includes("#") ? HashLink : Link}
            to={to}
            onClick={linkOnContainer ? (event) => event.stopPropagation() : undefined}
          >
            <span className="font-size-3">{linkText}</span>
          </Button>
        )}
        {onClick && linkText && (
          <Button
            variant={buttonStyle}
            endIcon={<EastIcon />}
            onClick={(event) => {
              if (linkOnContainer) event.stopPropagation();
              onClick();
            }}
          >
            <span className="font-size-3">{linkText}</span>
          </Button>
        )}
      </Box>
    </Grid2>
  );

  const containerLinkActive = Boolean(linkOnContainer && to);
  const containerSx = containerLinkActive
    ? ([{ cursor: "pointer" }, sx].filter(Boolean) as SxProps)
    : sx;

  return (
    <Box
      sx={containerSx}
      component="section"
      role={containerLinkActive ? "link" : undefined}
      tabIndex={containerLinkActive ? 0 : undefined}
      onClick={containerLinkActive ? () => navigate(to as string) : undefined}
      onKeyDown={
        containerLinkActive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                navigate(to as string);
              }
            }
          : undefined
      }
      className={classNames(
        "split-screen-block",
        className,
        reverse && "reverse"
      )}
    >
      <Grid2
        container
        columns={12}
        justifyContent="stretch"
        alignContent="stretch"
        alignItems="stretch"
      >
        {image && imageBlock}
        {component && componentBlock}
        {video && videoBlock}
        {contentBlock}
      </Grid2>
    </Box>
  );
};
